import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Film, X, AlertCircle, Loader2 } from 'lucide-react';

export interface VideoFramePickerProps {
  /** Local File (preferred — avoids S3 canvas CORS taint). */
  videoFile?: File | null;
  /** Fallback URL when there is no local File. */
  videoUrl: string;
  onClose: () => void;
  onSelect: (file: File) => void | Promise<void>;
  /** How many evenly-spaced frames to extract. Default: 6. */
  frameCount?: number;
}

type Frame = { dataUrl: string; blob: Blob; timestamp: number } | null;

const VideoFramePicker: React.FC<VideoFramePickerProps> = ({
  videoFile,
  videoUrl,
  onClose,
  onSelect,
  frameCount = 6
}) => {
  const { t } = useTranslation();
  const [frames, setFrames] = useState<Frame[]>(Array(frameCount).fill(null));
  const [extracting, setExtracting] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const blobUrl = videoFile ? URL.createObjectURL(videoFile) : null;
    const src = blobUrl || videoUrl;

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = src;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError(t('shared.framePicker.noCanvas', 'Browser does not support canvas') as string);
      setExtracting(false);
      return;
    }

    const captureAtTime = (time: number): Promise<{ dataUrl: string; blob: Blob } | null> => {
      return new Promise((resolve) => {
        const onSeeked = () => {
          try {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            canvas.toBlob((blob) => {
              if (!blob) return resolve(null);
              const dataUrl = URL.createObjectURL(blob);
              resolve({ dataUrl, blob });
            }, 'image/jpeg', 0.88);
          } catch (e) {
            console.error('Canvas draw failed:', e);
            resolve(null);
          } finally {
            video.removeEventListener('seeked', onSeeked);
          }
        };
        video.addEventListener('seeked', onSeeked);
        video.currentTime = time;
      });
    };

    const start = async () => {
      try {
        await new Promise<void>((resolve, reject) => {
          const onLoaded = () => { video.removeEventListener('loadedmetadata', onLoaded); resolve(); };
          const onErr = () => { video.removeEventListener('error', onErr); reject(new Error('Video load failed')); };
          video.addEventListener('loadedmetadata', onLoaded);
          video.addEventListener('error', onErr);
        });

        const duration = video.duration;
        if (!isFinite(duration) || duration <= 0) {
          throw new Error('Invalid video duration');
        }

        // Skip very first/last moments (often black/cuts) by using [5% .. 95%].
        const lo = duration * 0.05;
        const hi = duration * 0.95;
        const step = (hi - lo) / Math.max(1, frameCount - 1);
        const times = Array.from({ length: frameCount }, (_, i) => lo + step * i);

        for (let i = 0; i < times.length; i++) {
          if (cancelled) return;
          const result = await captureAtTime(times[i]);
          if (cancelled) return;
          setFrames(prev => {
            const next = [...prev];
            next[i] = result ? { ...result, timestamp: times[i] } : null;
            return next;
          });
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error('Frame extraction failed:', err);
          setError(err?.message || (t('shared.framePicker.extractError', 'Could not extract frames from video') as string));
        }
      } finally {
        if (!cancelled) setExtracting(false);
      }
    };

    start();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      video.src = '';
      setFrames(prev => {
        prev.forEach(f => { if (f?.dataUrl) URL.revokeObjectURL(f.dataUrl); });
        return prev;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickFrame = (index: number) => {
    const frame = frames[index];
    if (!frame) return;
    const file = new File([frame.blob], `thumbnail-frame-${index + 1}.jpg`, { type: 'image/jpeg' });
    onSelect(file);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Film className="h-5 w-5 text-indigo-500" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('shared.framePicker.title', 'Escolher frame do vídeo')}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          {error ? (
            <div className="text-center py-8">
              <AlertCircle className="h-10 w-10 text-red-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                {t('shared.framePicker.hint', 'Clique em um frame para usá-lo como thumbnail.')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {frames.map((frame, idx) => (
                  <button
                    key={idx}
                    onClick={() => pickFrame(idx)}
                    disabled={!frame}
                    className="relative aspect-video rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 hover:ring-2 hover:ring-indigo-400 disabled:opacity-60 disabled:cursor-wait transition-all"
                  >
                    {frame ? (
                      <>
                        <img src={frame.dataUrl} alt={`frame-${idx + 1}`} className="w-full h-full object-cover" />
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] font-mono bg-black/60 text-white rounded">
                          {frame.timestamp.toFixed(1)}s
                        </span>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              {extracting && (
                <p className="text-xs text-gray-400 text-center mt-3">
                  {t('shared.framePicker.extracting', 'Extraindo frames…')}
                </p>
              )}
            </>
          )}
        </div>

        <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoFramePicker;
